import React from "react";

import { ProjectPermission } from "../utils/permissions";
import { useProjectPermissions } from "storybook-dashboard/utils/hooks";

import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import DataTable from "react-data-table-component";
import { ActionIcon, formEventToData } from "./utils";
import { Hide } from "AppSrc/utils";
import Im from "immutable";
import Octicon from "react-octicon";
import Moment from "moment";
import { RoleTypesGuide } from "AppSrc/company/members/companyUser/tree";
import { getLastLoginFormatted } from "AppSrc/company/members/memberList";

const strToBool = (s) => (s === "true" ? true : s === "false" || !s ? false : s);

const sendProjectInvite = (e, { projectId, hideModal, triggerMembers, triggerInvites }) => {
  let data = formEventToData(e);

  let booleanKeys = Im.Set(["meta_json__auto_accept_if_user_exists"]);

  let _data = Object.entries(data).reduce(
    (a, [key, value]) => (!value ? a : a.setIn(key.split("__"), booleanKeys.has(key) ? strToBool(value) : value)),
    Im.Map()
  );

  doFetch(`/api/project/${projectId}/invite/`, "POST", _data).then(() => {
    if (triggerMembers) triggerMembers();
    if (triggerInvites) triggerInvites();
    if (hideModal) hideModal();
  });
};

const deleteMember = async ({ projectId, item, members }) => {
  // For grouped users, we need to delete ALL memberships for this user
  // The item represents a grouped user with multiple roles
  console.log("deleteMember", projectId, item.toJS(), members?.toJS());
  const userEmail = item.getIn(["user", "email"]);

  if (!userEmail) {
    console.error("No user email found for deletion:", item.toJS());
    alert("Error: Cannot find user email for deletion.");
    return;
  }

  // Find all original membership records for this user
  const userMemberships = (members || Im.List()).filter((member) => member.getIn(["user", "email"]) === userEmail);

  console.log("Deleting all memberships for user:", {
    userEmail,
    membershipCount: userMemberships.size,
    memberships: userMemberships.toJS(),
  });

  // Delete all memberships for this user
  const deletePromises = userMemberships
    .map((membership) => {
      const membershipId = membership.get("uid") || membership.get("id") || membership.get("pk");
      if (!membershipId) {
        console.error("No membership ID found:", membership.toJS());
        return Promise.reject(new Error("No membership ID found"));
      }

      console.log(`Deleting membership ${membershipId} for user ${userEmail}`);
      return doFetch(`/api/project/${projectId}/member/${membershipId}/`, "DELETE");
    })
    .toArray();

  if (deletePromises.length === 0) {
    alert("Error: No memberships found to delete.");
    return;
  }

  // Execute all deletions
  return Promise.all(deletePromises);
};

const deleteRole = ({ projectId, user, role, members, triggerMembers, triggerInvites }) => {
  const userEmail = user.getIn(["user", "email"]);

  if (!userEmail || !role) {
    console.error("Missing user email or role for role deletion:", { userEmail, role: role?.toJS() });
    alert("Error: Cannot find user or role information for deletion.");
    return;
  }

  // Find the specific membership record for this user and role
  const targetMembership = members.find(
    (membership) =>
      membership.getIn(["user", "email"]) === userEmail &&
      membership.getIn(["project_discipline", "name"]) === role.get("name")
  );

  if (!targetMembership) {
    console.error("Could not find membership record for role deletion:", {
      userEmail,
      roleName: role.get("name"),
      availableMemberships: members.filter((m) => m.getIn(["user", "email"]) === userEmail).toJS(),
    });
    alert("Error: Could not find the specific role assignment to remove.");
    return;
  }

  const membershipId = targetMembership.get("uid") || targetMembership.get("id") || targetMembership.get("pk");

  if (!membershipId) {
    console.error("No membership ID found for role deletion:", targetMembership.toJS());
    alert("Error: Cannot find membership ID for role deletion.");
    return;
  }

  console.log(`Removing role "${role.get("name")}" from user ${userEmail}`, { membershipId });

  doFetch(`/api/project/${projectId}/member/${membershipId}/`, "DELETE")
    .then(() => {
      console.log(`Successfully removed role "${role.get("name")}" from user ${userEmail}`);
      if (triggerMembers) triggerMembers();
      if (triggerInvites) triggerInvites();
    })
    .catch((error) => {
      console.error("Error removing role:", error);
      alert(`Error removing role: ${error.message}`);
    });
};

function RemoveRoleConfirmationModal({ user, role, onConfirm, onCancel }) {
  const fullName = `${user.getIn(["user", "first_name"], "")} ${user.getIn(["user", "last_name"], "")}`;
  const email = user.getIn(["user", "email"], "");
  const roleName = role.get("name", "");
  const remainingRoles = user.get("roles", Im.List()).filter((r) => r.get("name") !== roleName);

  return (
    <Modal show={true} onHide={onCancel} size="md">
      <div className="modal-header">
        <h5 className="modal-title">Remove Role</h5>
        <button type="button" className="btn-close" onClick={onCancel} />
      </div>
      <div className="modal-body">
        <div className="alert alert-warning">
          <Octicon name="alert" className="me-2" />
          <strong>Remove Role: {roleName}</strong>
          <p className="mb-0 mt-2">
            Are you sure you want to remove the <strong>{roleName}</strong> role from <strong>{fullName}</strong> (
            {email})?
          </p>
          {remainingRoles.size > 0 && (
            <p className="mb-0 mt-2">
              <small className="text-muted">
                They will still have these roles: {remainingRoles.map((r) => r.get("name")).join(", ")}
              </small>
            </p>
          )}
          {remainingRoles.size === 0 && (
            <p className="mb-0 mt-2">
              <small className="text-danger">
                <strong>Warning:</strong> This will remove their last role, effectively removing them from the project.
              </small>
            </p>
          )}
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-danger" onClick={onConfirm}>
          <Octicon name="trashcan" className="me-2" />
          Remove Role
        </button>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

function RemoveUserConfirmationModal({ user, onConfirm, onCancel }) {
  const fullName = `${user.getIn(["user", "first_name"], "")} ${user.getIn(["user", "last_name"], "")}`;
  const email = user.getIn(["user", "email"], "");
  const roles = user.get("roles", Im.List());

  return (
    <Modal title="Remove User from Project">
      <div className="p-3">
        <div className="d-flex flex-column align-items-start mb-3">
          <div className="d-flex flex-row">
            <Octicon className="text-warning mr-2" name="alert" style={{ fontSize: "24px" }} />
            <h6 className="mb-0">Are you sure you want to remove this user?</h6>
          </div>
          <p className="text-muted mb-3">
            This action will permanently remove the user from this project and revoke their access. They will no longer
            be able to view or interact with project data.
          </p>
          {roles.size > 0 && (
            <div className="alert alert-danger py-2">
              <small>
                <strong>All roles will be removed:</strong> {roles.map((r) => r.get("name")).join(", ")}
              </small>
            </div>
          )}
        </div>

        <div className="card bg-light border-0 mb-3">
          <div className="card-body py-3">
            <div className="row">
              <div className="col-sm-4">
                <small className="text-muted d-block">User</small>
                <strong>{fullName}</strong>
              </div>
              <div className="col-sm-4">
                <small className="text-muted d-block">Email</small>
                <span className="text-muted">{email}</span>
              </div>
              <div className="col-sm-4">
                <small className="text-muted d-block">Current Roles</small>
                {roles.size > 0 ? (
                  <div className="d-flex flex-wrap gap-1">
                    {roles.map((role, index) => (
                      <span key={index} className="badge badge-dark m-1" style={{ fontSize: "10px" }}>
                        {role?.get("name")}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">No roles assigned</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="alert alert-warning d-flex align-items-start">
          <Octicon name="info" className="me-2 mt-1" style={{ fontSize: "32px" }} />
          <div>
            <strong>Note:</strong> This will only remove the user from this specific project. They may still have access
            to other projects in the organisation.
          </div>
        </div>

        <div className="d-flex gap-2 justify-content-end">
          <button className="btn btn-secondary btn-sm mx-2" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-danger btn-sm" onClick={onConfirm}>
            <Octicon name="trashcan" className="me-2" />
            Remove User
          </button>
        </div>
      </div>
    </Modal>
  );
}

const exportToCSV = (data, projectName) => {
  if (!data || data.size === 0) return;

  // Define CSV headers
  const headers = ["Name", "Email", "Roles", "Last Login"];

  // Convert data to CSV format
  const csvContent = [
    headers.join(","),
    ...data.map((user) => {
      const fullName = `${user.getIn(["user", "first_name"], "")} ${user.getIn(["user", "last_name"], "")}`.trim();
      const email = user.getIn(["user", "email"], "");
      const roles = user.get("roles", Im.List());
      const rolesText = roles.size > 0 ? roles.map((r) => r?.get("name")).join("; ") : "No roles assigned";
      const lastLogin = user.getIn(["user", "last_login"]);
      const formattedLastLogin = getLastLoginFormatted(lastLogin);

      // Escape CSV fields that contain commas or quotes
      const escapeCSVField = (field) => {
        if (typeof field === "string" && (field.includes(",") || field.includes('"') || field.includes("\n"))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      };

      return [
        escapeCSVField(fullName),
        escapeCSVField(email),
        escapeCSVField(rolesText),
        escapeCSVField(formattedLastLogin),
      ].join(",");
    }),
  ].join("\n");

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${projectName || "project"}_users_${Moment().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

function ProjectHeader({ projectId, userCount, isLoading }) {
  const { data: project } = useApi(`/api/project/{projectId}/`, { projectId });

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-2">
              <h1 className="h3 mb-0 text-primary">Users</h1>
            </div>
            <h6 className="text-muted font-weight-bold mb-2">{project?.get("name")}</h6>
            {!isLoading && (
              <p className="text-muted small mb-0">
                <strong>Total users:</strong> {userCount}
              </p>
            )}
          </div>
          <div className="text-end">
            <span className="badge badge-primary">Project Users</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserDescription() {
  return (
    <div className="mb-4">
      <h2 className="h4 text-dark mb-2">User Management</h2>
      <p className="text-muted">
        Manage project users and their roles. You can invite new users, assign them to specific roles, and control their
        access within this project.
      </p>
      <div className="alert alert-info">
        <div className="d-flex">
          <Octicon name="info" className="me-2 mt-1" />
          <div>
            <strong>Role Management:</strong> Each user can have multiple roles within a project.
            <ul className="mb-0 mt-2">
              <li>
                <strong>Remove Individual Roles:</strong> Click the × button on any role badge to remove just that
                specific role
              </li>
              <li>
                <strong>Remove User Completely:</strong> Use the "Remove User" button to remove all roles and completely
                remove the user from this project
              </li>
              <li>
                <strong>Project Roles:</strong> Users can have different roles across different projects
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="card-body border rounded py-3">
        <RoleTypesGuide isProject={true} />
      </div>
    </div>
  );
}

function InviteNewUserModal(props) {
  let { hideModal } = useModal();
  let { projectId } = props;
  let { data: disciplines } = useApi("/api/project/{projectId}/discipline/", { projectId });

  return (
    <Modal title="Invite New User">
      <div className="p-3">
        <p className="text-muted mb-3">
          Invite a new user to join this project. They will receive an email invitation with instructions to access the
          project and will be assigned to the selected role.
        </p>
        <form onSubmit={(e) => sendProjectInvite(e, { ...props, hideModal })}>
          <div className="row">
            <div className="col-sm-8">
              <div className="form-group mb-3">
                <label htmlFor="dName" className="form-label">
                  Email Address
                </label>
                <input
                  className="form-control"
                  name="email"
                  id="dName"
                  type="email"
                  placeholder="Enter user's email address"
                  defaultValue={""}
                  required
                />
              </div>
            </div>
            <div className="col-sm-4">
              <div className="form-group mb-3">
                <label htmlFor="dApprover" className="form-label">
                  Project Role
                </label>
                <select name="project_discipline" className="form-control" defaultValue={""}>
                  <option value="">Select a role...</option>
                  {(disciplines || Im.List()).map((i, k) => (
                    <option key={k} value={i.get("uid")}>
                      {i.get("name")}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="form-group mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                name="meta_json__auto_accept_if_user_exists"
                type="checkbox"
                value={true}
                id="autoAcceptInvite"
              />
              <label className="form-check-label" htmlFor="autoAcceptInvite">
                Auto-accept invitation if user already exists in the system
              </label>
            </div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary" type="submit">
              <Octicon name="mail" className="me-2" />
              Send Invitation
            </button>
            <button className="btn btn-secondary" type="button" onClick={hideModal}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

const getColumns = (props) => [
  {
    name: "Name",
    sortable: true,
    selector: (item) => `${item.getIn(["user", "first_name"])} ${item.getIn(["user", "last_name"])}`,
    grow: 2,
    wrap: true,
    cell: (item) => {
      const fullName = `${item.getIn(["user", "first_name"])} ${item.getIn(["user", "last_name"])}`;
      return (
        <div className="d-flex flex-column">
          <span className="fw-medium">{fullName}</span>
        </div>
      );
    },
  },
  {
    name: "Email",
    sortable: true,
    selector: (item) => item.getIn(["user", "email"]),
    grow: 2,
    wrap: true,
    cell: (item) => <span className="text-muted">{item.getIn(["user", "email"])}</span>,
  },
  {
    name: "Roles at this level",
    sortable: true,
    selector: (item) => {
      const roles = item.get("roles", Im.List());
      return roles.size > 0 ? roles.map((r) => r?.get("name")).join(", ") : "No roles assigned";
    },
    grow: 2,
    wrap: true,
    cell: (item) => {
      const roles = item.get("roles", Im.List());

      if (roles.size === 0) {
        return <span className="text-muted">No roles assigned</span>;
      }

      return (
        <div className="d-flex flex-wrap gap-1">
          {roles.map((role, index) => (
            <div key={index} className="position-relative role-badge-container">
              <span
                className={`badge ${props?.isAdmin ? "bg-light text-dark" : "bg-secondary text-white"} m-1`}
                style={{
                  fontSize: "11px",
                  minHeight: "24px",
                  lineHeight: "1.2",
                  paddingRight: props?.isAdmin ? "24px" : "8px",
                  transition: "all 0.2s ease",
                }}
              >
                {role?.get("name")}
                <Hide when={!props?.isAdmin}>
                  <button
                    className="btn position-absolute d-flex align-items-center justify-content-center"
                    style={{
                      fontSize: "10px",
                      right: "2px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      opacity: 0.7,
                      background: "rgba(220, 53, 69, 0.8)",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "16px",
                      height: "16px",
                      padding: "0",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = "1";
                      e.target.style.background = "rgba(220, 53, 69, 1)";
                      e.target.style.transform = "translateY(-50%) scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = "0.7";
                      e.target.style.background = "rgba(220, 53, 69, 0.8)";
                      e.target.style.transform = "translateY(-50%) scale(1)";
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onRemoveRole && props.onRemoveRole(item, role);
                    }}
                    title={`Remove ${role?.get("name")} role`}
                  >
                    ×
                  </button>
                </Hide>
              </span>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    name: "Last Login",
    sortable: true,
    selector: (row) => row.getIn(["user", "last_login"]),
    format: (row) => getLastLoginFormatted(row.getIn(["user", "last_login"])),
    grow: 1,
    wrap: true,
    cell: (row) => {
      const lastLogin = row.getIn(["user", "last_login"]);
      const formatted = getLastLoginFormatted(lastLogin);
      const isRecent = Moment.utc(lastLogin).isAfter(Moment().subtract(30, "days"));
      return <small className={"text-muted"}>{formatted}</small>;
    },
  },
  {
    name: "Actions",
    cell: (item) => (
      <Hide when={!props?.isAdmin}>
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={() => props.onRemoveUser && props.onRemoveUser(item)}
          title="Remove User (All Roles)"
        >
          <Octicon name="person-x" />
          <span className="d-none d-md-inline ms-1">Remove User</span>
        </button>
      </Hide>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "140px",
  },
];

export default function MemberList({ projectId }) {
  let { setModal, hideModal } = useModal();
  let { isAdmin } = useProjectPermissions();
  const [searchTerm, setSearchTerm] = React.useState("");
  const { data: project } = useApi(`/api/project/{projectId}/`, { projectId });
  let { trigger: triggerInvites, isLoading: isLoadingInvites } = useApi("/api/project/{projectId}/invite/", {
    projectId,
  });
  let { data: members, trigger: triggerMembers, isLoading } = useApi("/api/project/{projectId}/member/", { projectId });

  const handleRemoveUser = (user) => {
    setModal(
      <RemoveUserConfirmationModal
        user={user}
        onConfirm={() => {
          deleteMember({ projectId, item: user, members })
            .then((results) => {
              console.log(
                `Successfully deleted all ${results.length} memberships for ${user.getIn(["user", "email"])}`
              );
              if (triggerMembers) triggerMembers();
              if (triggerInvites) triggerInvites();
            })
            .catch((error) => {
              console.error("Error deleting user memberships:", error);
              alert(`Error removing user: ${error.message}`);
            });
          hideModal();
        }}
        onCancel={hideModal}
      />
    );
  };

  const handleRemoveRole = (user, role) => {
    setModal(
      <RemoveRoleConfirmationModal
        user={user}
        role={role}
        onConfirm={() => {
          deleteRole({ projectId, user, role, members, triggerMembers, triggerInvites });
          hideModal();
        }}
        onCancel={hideModal}
      />
    );
  };

  // Group users by user email and combine their roles, merging with company member data for last login
  const groupedMembers = React.useMemo(() => {
    if (!members) return Im.List();

    const userMap = members.reduce((acc, member) => {
      const userEmail = member.getIn(["user", "email"]);
      const existingUser = acc.get(userEmail);

      if (existingUser) {
        // User already exists, add this role to their roles array
        const currentRoles = existingUser.get("roles", Im.List());
        const newRole = member.get("project_discipline");

        // Only add role if it's not already in the list (avoid duplicates by checking role name)
        if (newRole && !currentRoles.find((role) => role?.get("name") === newRole?.get("name"))) {
          const roles = currentRoles.push(newRole);
          return acc.set(userEmail, existingUser.set("roles", roles));
        }
        return acc;
      } else {
        // New user, create entry with first role
        const roles = member.get("project_discipline") ? Im.List([member.get("project_discipline")]) : Im.List();
        let enhancedMember = member.set("roles", roles);

        return acc.set(userEmail, enhancedMember);
      }
    }, Im.Map());

    const result = userMap.valueSeq().toList();
    console.log("Grouped members with last login data:", result.toJS());
    return result;
  }, [members]);

  // Filter data based on search term
  const filteredMembers =
    groupedMembers?.filter((user) => {
      if (!searchTerm) return true;

      const firstName = user.getIn(["user", "first_name"], "").toLowerCase();
      const lastName = user.getIn(["user", "last_name"], "").toLowerCase();
      const email = user.getIn(["user", "email"], "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      const search = searchTerm.toLowerCase();

      return fullName.includes(search) || email.includes(search);
    }) || Im.List();

  const userCount = groupedMembers?.size || 0;
  const filteredUserCount = filteredMembers?.size || 0;

  console.log("ProjectUserList", members?.toJS());

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <ProjectHeader projectId={projectId} userCount={userCount} isLoading={isLoading} />

          <div className="card shadow-sm">
            <div className="card-body">
              <UserDescription />

              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="mb-0">Project Team</h5>
                </div>
                <ProjectPermission projectId={projectId} requiresAdmin={true}>
                  <button
                    className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                    onClick={() => setModal(<InviteNewUserModal {...{ projectId, triggerMembers, triggerInvites }} />)}
                  >
                    <Octicon name="plus" />
                    Invite New User
                  </button>
                </ProjectPermission>
              </div>

              {/* Search Bar */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <Octicon name="search" className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search users by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ borderLeft: "none" }}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchTerm("")}
                        title="Clear search"
                      >
                        <Octicon name="x" />
                      </button>
                    )}
                  </div>
                  {searchTerm && (
                    <small className="text-muted mt-1 d-block">
                      Showing {filteredUserCount} of {userCount} users
                    </small>
                  )}
                </div>
                <div className="col-md-6 d-flex justify-content-end align-items-start">
                  <button
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() => exportToCSV(filteredMembers, project?.get("name"))}
                    disabled={filteredUserCount === 0}
                    title="Export user list to CSV"
                  >
                    <Octicon className="mr-1" name="cloud-download" /> Export CSV
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary me-3 mr-3" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="text-muted">Loading users...</span>
                </div>
              ) : (
                <div className="table-responsive">
                  <DataTable
                    columns={getColumns({
                      projectId,
                      triggerMembers,
                      triggerInvites,
                      isAdmin,
                      onRemoveUser: handleRemoveUser,
                      onRemoveRole: handleRemoveRole,
                    })}
                    data={filteredMembers?.toArray() || []}
                    highlightOnHover={true}
                    pagination={filteredUserCount > 25}
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-5">
                        <Octicon
                          name={searchTerm ? "search" : "person"}
                          className="text-muted mb-3"
                          style={{ fontSize: "48px" }}
                        />
                        <h5 className="text-muted">{searchTerm ? "No matching users found" : "No users found"}</h5>
                        <p className="text-muted">
                          {searchTerm
                            ? `No users match your search for "${searchTerm}". Try adjusting your search terms.`
                            : "This project doesn't have any users yet. Start by inviting users."}
                        </p>
                        {searchTerm ? (
                          <button className="btn btn-outline-primary" onClick={() => setSearchTerm("")}>
                            <Octicon name="x" className="me-2" />
                            Clear Search
                          </button>
                        ) : (
                          <ProjectPermission projectId={projectId} requiresAdmin={true}>
                            <button
                              className="btn btn-outline-primary"
                              onClick={() =>
                                setModal(<InviteNewUserModal {...{ projectId, triggerMembers, triggerInvites }} />)
                              }
                            >
                              <Octicon name="plus" className="me-2" />
                              Invite First User
                            </button>
                          </ProjectPermission>
                        )}
                      </div>
                    }
                    customStyles={{
                      table: {
                        style: {
                          borderRadius: "8px",
                        },
                      },
                      headRow: {
                        style: {
                          backgroundColor: "#f8f9fa",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                        },
                      },
                      rows: {
                        style: {
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                          },
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
